import type { OnlyEconomyForm } from "@/hooks/form/use-economy-form";
import type { CacheForms } from "@/types/cache-forms";
import type { Applicant, Debts } from "@/types/form";
import type {
	GoldfingerApplicant,
	GoldfingerApplication,
	GoldfingerCommonData,
	GoldfingerCountry,
	GoldfingerLoanApplication,
	GoldfingerLoanDebt,
} from "@/types/goldfinger";
import { normalizeNationalId } from "@/utils/validation/normalize-national-id";

interface Props extends CacheForms {
	appId?: string;
	country: string;
	step?: number;
}

const toIsoDate = (date?: Date | string): string | null => {
	if (!date) return null;
	const parsed = date instanceof Date ? date : new Date(date);
	return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const normalizeGoldfingerNationalId = (
	value: string | null | undefined,
	country: string
): string | null => {
	if (value == null) return null;

	if (country === "Sweden") return normalizeNationalId("SE", value);
	if (country === "Finland") return normalizeNationalId("FI", value);

	return value.trim();
};

const stripNullish = <T>(value: T): T => {
	if (Array.isArray(value)) {
		return value
			.map((item) => stripNullish(item))
			.filter(
				(item) => item !== null && item !== undefined
			) as unknown as T;
	}

	if (value && typeof value === "object" && !(value instanceof Date)) {
		const output: Record<string, unknown> = {};
		for (const [key, rawValue] of Object.entries(value)) {
			const cleaned = stripNullish(rawValue);
			const isEmptyObject =
				cleaned !== null &&
				typeof cleaned === "object" &&
				!Array.isArray(cleaned) &&
				Object.keys(cleaned).length === 0;
			if (cleaned !== null && cleaned !== undefined && !isEmptyObject) {
				output[key] = cleaned;
			}
		}
		return output as T;
	}

	return value;
};

const mapDebts = (
	debts?: Debts,
	country?: string
): GoldfingerLoanDebt[] | null => {
	if (!debts) return null;

	const debtByType: Array<[string, number | undefined]> = [
		["MortgageLoan", debts.mortgageLoan],
		["VehicleLoan", debts.vehicleLoan],
		["StudentLoan", debts.studentLoan],
		["ConsumerLoan", debts.consumerLoan],
	];

	const mapped = debtByType
		.filter(([, amount]) => typeof amount === "number")
		.map(
			([debtType, amount]) =>
				({
					debtType,
					outstandingAmount: amount ?? null,
					...(country === "Finland" && { redeem: false }),
				}) satisfies GoldfingerLoanDebt
		);

	return mapped.length > 0 ? mapped : null;
};

const buildGoldfingerApplicant = (
	person: Partial<Applicant> | undefined,
	economy: OnlyEconomyForm | undefined,
	country: string
): GoldfingerApplicant => ({
	nationalId: normalizeGoldfingerNationalId(person?.nationalId, country),
	firstName: person?.firstName ?? null,
	lastName: person?.lastName ?? null,
	maritalStatus: person?.maritalStatus ?? null,
	// Already E.164 from the browser; the phone library is client-only.
	mobileNumber: person?.mobileNumber ?? null,
	emailAddress: person?.emailAddress ?? null,
	numberOfChildrenBelow18: person?.numberOfChildrenBelow18 ?? null,
	numberOfCars: economy?.employment?.numberOfCars ?? null,
	dwelling: economy?.dwelling
		? {
				type: economy.dwelling.type || null,
			}
		: null,
	employment: economy?.employment
		? {
				type: economy.employment.type || null,
				employer: economy.employment.employer ?? null,
				startDate: toIsoDate(economy.employment.startDate),
			}
		: null,

	expenses: economy?.dwelling?.type
		? [
				{
					amount: economy.dwelling.expenses?.amount ?? 0,
					type: economy.dwelling.expenses?.type ?? null,
					period: economy.dwelling.expenses?.period ?? null,
				},
			]
		: null,

	income:
		typeof economy?.income?.amount === "number"
			? [
					{
						amount: economy.income.amount,
						type: economy.income.type || null,
						period: economy.income.period ?? null,
					},
				]
			: null,
	debts: mapDebts(economy?.debts, country),
});

export const mapFormToGoldfingerLoanApplication = ({
	landingPageForm,
	aboutForm,
	personalInformationForm,
	economyForm,
	appId,
	country,
	step,
}: Props): GoldfingerLoanApplication => {
	const economy = economyForm?.applicant?.economy;

	const application: GoldfingerApplication = {
		externalApplicationId: appId ?? null,
		appliedAmount: aboutForm?.appliedAmount ?? null,
		appliedRepaymentTime: aboutForm?.appliedRepaymentTime ?? null,
		purpose: aboutForm?.purpose ?? null,
		refinanceAmount: aboutForm?.refinanceAmount ?? 0,
		campaignId: landingPageForm?.campaignId ?? null,
		...(country === "Sweden" &&
			landingPageForm?.campaignId?.toLowerCase().includes("outbound") && {
				disableCustomerReminders: true,
			}),
	};

	const applicant: GoldfingerApplicant = buildGoldfingerApplicant(
		personalInformationForm?.applicant,
		economy,
		country
	);

	const coapplicant: GoldfingerApplicant | null =
		economyForm?.hasCoApplicant && economyForm?.coApplicant
			? buildGoldfingerApplicant(
					economyForm.coApplicant.applicant,
					economyForm.coApplicant.economy,
					country
				)
			: null;

	const commonData: GoldfingerCommonData = {
		step,
		utm_campaign: landingPageForm?.utmCampaign ?? null,
		utm_adtraction: landingPageForm?.adtractionId ?? null,
		utm_source: landingPageForm?.utmSource ?? null,
		utm_medium: landingPageForm?.utmMedium ?? null,
		utm_term: landingPageForm?.utmTerm ?? null,
		utm_content: landingPageForm?.utmContent ?? null,
		googleClickId: landingPageForm?.googleClickId ?? null,
		facebookClickId: landingPageForm?.facebookClickId ?? null,
		params: {
			dataUsageConsent: landingPageForm?.isAffirmedIntegrityPolicyCheckbox
				? "true"
				: "false",
			marketingConsent:
				landingPageForm?.isAffirmedMarketingCommunicationCheckbox
					? "true"
					: "false",
			thirdPartyShareConsent:
				landingPageForm?.isAffirmedIntegrityPolicyCheckbox
					? "true"
					: "false",
			...(typeof economyForm?.hasCoApplicant === "boolean"
				? {
						hasCoApplicant: economyForm.hasCoApplicant
							? "true"
							: "false",
					}
				: {}),
		},
	};

	const payload = stripNullish<GoldfingerLoanApplication>({
		appId: appId ?? null,
		country: country as GoldfingerCountry,
		application,
		applicant,
		coapplicant,
		commonData,
	});

	return economyForm?.hasCoApplicant === false
		? { ...payload, coapplicant: null }
		: payload;
};
